import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Star, Trash } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { adminApi, formatDate } from '../utils';

// Review Moderation Panel Component
const ReviewModerationPanel = ({ token }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState({});

  const fetchReviews = useCallback(async () => {
    try {
      const response = await adminApi.get('/admin/reviews');
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review? This cannot be undone.')) {
      return;
    }
    
    setDeleting(prev => ({ ...prev, [reviewId]: true }));
    try {
      await adminApi.delete(`/admin/reviews/${reviewId}`);
      toast.success('Review deleted');
      setReviews(prev => prev.filter(r => r._id !== reviewId));
    } catch (error) {
      toast.error('Failed to delete review');
    } finally {
      setDeleting(prev => ({ ...prev, [reviewId]: false }));
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading reviews...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Review Moderation ({reviews.length} total)</h2>
        <Button variant="outline" size="sm" onClick={fetchReviews}>
          Refresh
        </Button>
      </div>

      {reviews.length === 0 ? (
        <Card className="p-8 text-center">
          <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-lg font-medium">No reviews yet</p>
          <p className="text-muted-foreground">Customer reviews will appear here.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => (
            <Card key={review._id} className="p-4" data-testid={`review-${review._id}`}>
              <div className="flex gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex">
                          {[1,2,3,4,5].map(star => (
                            <Star 
                              key={star} 
                              className={`w-4 h-4 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                            />
                          ))}
                        </div>
                        <span className="text-sm font-medium">{review.title}</span>
                        {review.verified_purchase && (
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Verified</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">
                        <strong>{review.product_name}</strong> • {review.user_email || 'Anonymous'} • {formatDate(review.created_at)}
                      </p>
                      <p className="text-sm text-gray-700 line-clamp-2">{review.content}</p>
                      {review.photos && review.photos.length > 0 && (
                        <div className="flex gap-2 mt-2">
                          {review.photos.map((photo, idx) => {
                            const url = typeof photo === 'string' ? photo : photo.url;
                            const photoUrl = url?.startsWith('http') ? url : url;
                            return (
                              <img 
                                key={idx}
                                src={photoUrl}
                                alt={`Review photo ${idx + 1}`}
                                className="w-12 h-12 object-cover rounded border"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(review._id)}
                      disabled={deleting[review._id]}
                      data-testid={`delete-review-${review._id}`}
                    >
                      {deleting[review._id] ? 'Deleting...' : <><Trash className="w-4 h-4 mr-1" /> Delete</>}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewModerationPanel;
